"use client";

import { useEffect, useRef, useState, type CSSProperties, type FocusEvent } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { ArrowRight, ShieldCheck } from "lucide-react";
import CtaProcessList, { CTA_STEP_COUNT } from "./cta/CtaProcessList";
import { useAutoStep } from "../hooks/useAutoStep";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const ACCENT_WORDS = ["Transparent", "Şəffaf", "прозрачными"];

function AccentTitle({ title }: { title: string }) {
  const word = ACCENT_WORDS.find((item) => title.includes(item));
  if (!word) return <>{title}</>;
  const index = title.indexOf(word);
  return (
    <>
      {title.slice(0, index)}
      <span className="text-brand-accent">{word}</span>
      {title.slice(index + word.length)}
    </>
  );
}

const GRID: CSSProperties = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)",
  backgroundSize: "48px 48px",
};

export function CTASection() {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("cta");
  const reducedMotion = usePrefersReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const [entered, setEntered] = useState(false);
  const [inView, setInView] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setEntered(true);
      },
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setHidden(document.visibilityState === "hidden");
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  const active = useAutoStep(CTA_STEP_COUNT, 2200, {
    paused: hovered || focused || hidden || !inView,
    disabled: reducedMotion,
  });

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
  };

  return (
    <section ref={sectionRef} className="w-full bg-brand-cream">
      <div className="mx-auto max-w-[1240px] px-8 py-[72px]">
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          className={`relative overflow-hidden rounded-[20px] bg-brand-navy p-7 px-5 shadow-[14px_14px_0_rgba(15,27,45,0.14)] sm:rounded-[28px] sm:p-[clamp(32px,5vw,72px)] ${
            entered ? "animate-hero-fade-up motion-reduce:animate-none" : "opacity-0 motion-reduce:opacity-100"
          }`}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-12 -top-12 h-[calc(100%+96px)] w-[calc(100%+96px)] animate-cta-grid will-change-transform motion-reduce:animate-none"
            style={GRID}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-20 -top-[120px] h-[420px] w-[420px] rounded-full bg-brand-accent opacity-[0.22] blur-[90px] animate-cta-glow will-change-transform motion-reduce:animate-none"
          />

          <div className="relative z-10 flex flex-col items-start gap-14 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 basis-[440px] flex-col items-start gap-7">
              <span className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-brand-muted-light px-4 py-2 text-[15px] font-semibold uppercase tracking-[0.04em] text-brand-dot-track">
                <ShieldCheck className="h-4 w-4 text-brand-accent" strokeWidth={2.6} aria-hidden="true" />
                {t("eyebrow")}
              </span>

              <h2 className="text-[clamp(36px,5vw,66px)] font-bold leading-[1.04] tracking-[-0.02em] text-brand-cream">
                <AccentTitle title={t("title")} />
        </h2>

              <p className="max-w-[520px] text-xl leading-[1.55] text-brand-dot-track">{t("description")}</p>

              <button
                type="button"
                onClick={() => router.push(`/${locale}/login`)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-accent px-[34px] py-[18px] text-lg font-bold text-brand-navy shadow-[0_6px_0_var(--brand-cream)] transition-[transform,box-shadow] duration-150 hover:translate-y-[2px] hover:shadow-[0_4px_0_var(--brand-cream)] active:translate-y-[6px] active:shadow-none focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-brand-cream sm:w-auto"
              >
          {t("button")}
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="mx-auto w-full min-w-0 max-w-[480px] flex-1 basis-[380px] lg:mx-0">
              <CtaProcessList active={active} entered={entered || reducedMotion} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
